import { Box, Chip, Divider, Fade, Typography, useTheme } from "@mui/material";
import React from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { Company } from "../../../../types";
import { SequenceResult } from "../types";
import SequenceMailboxConnect from "./SequenceMailboxConnect";
import StepNavButtons from "./StepNavButtons";

interface SequencePreviewStepProps {
  sequence?: SequenceResult;
  companyId: string;
  company: Company;
  getToken: () => Promise<string>;
  onNext: () => void;
  onBack: () => void;
}

// When a step lands the day the previous one was sent (or has no delay), show "Same day"
// rather than "in 0 days"; otherwise render the spacing in human terms.
function timingLabel(daysAfterPrevious?: number, index?: number): string {
  if (index === 0) return "Day 1";
  if (!daysAfterPrevious || daysAfterPrevious <= 0) return "Same day";
  return `${daysAfterPrevious} day${daysAfterPrevious === 1 ? "" : "s"} later`;
}

// Preview of the generated outreach sequence. The onboarding sequence is always 5
// AI-personalized email steps, so we render a simple ordered cadence the user can recognize
// and edit later from the Sequences page.
export default function SequencePreviewStep({
  sequence,
  companyId,
  company,
  getToken,
  onNext,
  onBack,
}: SequencePreviewStepProps) {
  const theme = useTheme();
  const steps = sequence?.steps || [];
  const enrolledLeadCount = sequence?.enrolledLeadCount || 0;

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          Sequences
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Your sales sequence
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          A 5-email outreach cadence, personalized to your ideal customer. It stays paused until
          you connect a mailbox and turn on sending below.
        </Typography>

        {!steps.length ? (
          <Typography color="text.secondary">
            We couldn't generate a sequence this time. You can build one anytime from the
            Sequences page.
          </Typography>
        ) : (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.hover",
            }}
          >
            {sequence?.name && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {sequence.name}
              </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {steps.map((step, i) => (
                <Box key={i}>
                  {i > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <LucideIcon name="Mail" size={20} color={theme.palette.primary.main} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {step.stepName || `Email ${i + 1}`}
                        </Typography>
                        <Chip
                          label={timingLabel(step.daysAfterPrevious, i)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {step.aiPersonalized
                          ? "AI-personalized email"
                          : "Email"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {enrolledLeadCount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <LucideIcon name="UserPlus" size={18} color={theme.palette.primary.main} />
                <Typography variant="body2" color="text.secondary">
                  {enrolledLeadCount} lead{enrolledLeadCount === 1 ? "" : "s"} from your list{" "}
                  {enrolledLeadCount === 1 ? "is" : "are"} already enrolled — they'll start
                  receiving emails once you turn on sending.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {companyId && (
          <SequenceMailboxConnect
            companyId={companyId}
            company={company}
            getToken={getToken}
            sequenceId={sequence?.id}
          />
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
